import { createAction, ActionType } from 'typesafe-actions';
import {
    ProductsWishlistResponse,
    FETCH_PRODUCTS_WISHLIST,
    FETCH_PRODUCTS_WISHLIST_SUCCESS,
    FETCH_PRODUCTS_WISHLIST_FAILED,
    ADD_TO_PRODUCTS_WISHLIST,
    ADD_TO_PRODUCTS_WISHLIST_SUCCESS,
    ADD_TO_PRODUCTS_WISHLIST_FAILED,
    REMOVE_FROM_PRODUCTS_WISHLIST,
    REMOVE_FROM_PRODUCTS_WISHLIST_FAILED,
    REMOVE_FROM_PRODUCTS_WISHLIST_SUCCESS,
    PRODUCTS_WISHLIST_QUEUE_UPDATED,
    ADD_QUEUED_PRODUCT_TO_WISHLIST,
    REMOVE_QUEUED_PRODUCT_FROM_WISHLIST,
    CLEAR_WISHLIST,
} from './types';
import { DataFetchingThunkAction, BellaThunkDispatch, StateInterface } from 'Client/redux/types';
import { getErrorCustomData } from 'Common/errors/CustomFetchApiError';
import retry from 'Common/utils/retry';
import { RetryError } from 'Common/errors';
import { WishListNotificationMessages } from 'Client/components/ProductDetailPage/hooks/useProductsWishlist';
import { addNotification, defaultNotificationShowInterval } from 'Client/redux/notifications/actions';

const isFetchingProductsWishlistAction = createAction(FETCH_PRODUCTS_WISHLIST);

const fetchProductsWishlistSuccessAction = createAction(
    FETCH_PRODUCTS_WISHLIST_SUCCESS,
    action => (response: ProductsWishlistResponse) =>
        action({
            response,
        })
);

const fetchProductsWishlistFailedAction = createAction(FETCH_PRODUCTS_WISHLIST_FAILED, action => (error: Error) =>
    action({
        error,
    })
);

export const fetchProductsWishlistAction = (): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    dispatch(isFetchingProductsWishlistAction());

    try {
        const getProductsWishlist = retry(() => apiService.shopApi.getProductsWishlist(), 3);
        const wishlistResponse = await getProductsWishlist();
        dispatch(fetchProductsWishlistSuccessAction(wishlistResponse));
    } catch (err) {
        const errorInfo = RetryError.isRetryError(err) ? err.toJson() : getErrorCustomData(err);
        logger.error(err, { action: 'fetchProductsWishlistFailedAction', ...errorInfo });
        dispatch(fetchProductsWishlistFailedAction(err));
    }
};

const isAddingToProductsWishlistAction = createAction(ADD_TO_PRODUCTS_WISHLIST, action => (simpleSku: string) =>
    action({
        simpleSku,
    })
);

const addToProductsWishlistSuccessAction = createAction(ADD_TO_PRODUCTS_WISHLIST_SUCCESS);

const addToProductsWishlistFailedAction = createAction(
    ADD_TO_PRODUCTS_WISHLIST_FAILED,
    action => (error: Error, simpleSku: string) =>
        action({
            error,
            simpleSku,
        })
);

const isRemovingFromProductsWishlistAction = createAction(
    REMOVE_FROM_PRODUCTS_WISHLIST,
    action => (simpleSku: string) =>
        action({
            simpleSku,
        })
);

const removeFromProductsWishlistSuccessAction = createAction(REMOVE_FROM_PRODUCTS_WISHLIST_SUCCESS);

const removeFromProductsWishlistFailedAction = createAction(
    REMOVE_FROM_PRODUCTS_WISHLIST_FAILED,
    action => (error: Error, simpleSku: string) =>
        action({
            error,
            simpleSku,
        })
);

export const updateProductsWishlistQueue = createAction(
    PRODUCTS_WISHLIST_QUEUE_UPDATED,
    action => (todo: string | null, simpleSku: string | null) =>
        action({
            todo,
            simpleSku,
        })
);

export const clearWishlistAction = createAction(CLEAR_WISHLIST);

export const actions = {
    isFetchingProductsWishlistAction,
    fetchProductsWishlistSuccessAction,
    fetchProductsWishlistFailedAction,
    isAddingToProductsWishlistAction,
    addToProductsWishlistSuccessAction,
    addToProductsWishlistFailedAction,
    isRemovingFromProductsWishlistAction,
    removeFromProductsWishlistFailedAction,
    updateProductsWishlistQueue,
    removeFromProductsWishlistSuccessAction,
    clearWishlistAction,
};

export const addToProductsWishlistAction = (
    simpleSku: string,
    notificationMessages?: WishListNotificationMessages
): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    dispatch(isAddingToProductsWishlistAction(simpleSku));
    try {
        await apiService.shopApi.addToProductsWishlist(simpleSku);
        dispatch(addToProductsWishlistSuccessAction());
        if (notificationMessages) {
            dispatch(addNotification(notificationMessages.successMessage));
        }
    } catch (err) {
        logger.error(err, { action: 'addToProductsWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(addToProductsWishlistFailedAction(err, simpleSku));
        if (notificationMessages) {
            dispatch(addNotification(notificationMessages.errorMessage, defaultNotificationShowInterval, 'error'));
        }
    }
};

export const removeFromProductsWishlistAction = (simpleSku: string): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    dispatch(isRemovingFromProductsWishlistAction(simpleSku));
    try {
        await apiService.shopApi.removeFromProductsWishlist(simpleSku);
        dispatch(removeFromProductsWishlistSuccessAction());
    } catch (err) {
        logger.error(err, { action: 'removeFromProductsWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(removeFromProductsWishlistFailedAction(err, simpleSku));
    }
};

export const fetchOrAddProductsWishlist = () => (dispatch: BellaThunkDispatch, getState: () => StateInterface) => {
    const { productsWishlistQueue } = getState();

    if (!productsWishlistQueue.simpleSku) {
        dispatch(fetchProductsWishlistAction());
        return;
    }

    if (productsWishlistQueue.todo === ADD_QUEUED_PRODUCT_TO_WISHLIST) {
        dispatch(addToProductsWishlistAction(productsWishlistQueue.simpleSku));
    }

    if (productsWishlistQueue.todo === REMOVE_QUEUED_PRODUCT_FROM_WISHLIST) {
        dispatch(removeFromProductsWishlistAction(productsWishlistQueue.simpleSku));
    }

    dispatch(updateProductsWishlistQueue(null, null));
};

export type ProductsWishlistActionType = ActionType<typeof actions>;
